import { IAny, IAnyObject } from '@yimoka/shared';
import { IAPIExecutor, INotifier, IStoreResponse } from '@yimoka/store';
import React, { ComponentType, createContext, ReactNode, useContext, useMemo } from 'react';

const ConfigContext = createContext<IConfig | null>(null);

// 内部使用的原始 Provider，不对外暴露
const ConfigContextProvider = ConfigContext.Provider;

export const ConfigConsumer = ConfigContext.Consumer;

export const useConfig = () => useContext(ConfigContext) as IConfig;

export const useReport = () => {
  const config = useConfig();
  return config?.report;
};

export const useNotifier = () => {
  const config = useConfig();
  return config?.notifier;
};

export const useAPIExecutor = () => {
  const config = useConfig();
  return config?.apiExecutor;
};

export const useLocation = () => {
  const config = useConfig();
  return config?.useLocation?.() as ReturnType<IUseLocation>;
};


export const useRouteParams = () => {
  const config = useConfig();
  return config?.useRouteParams?.() as ReturnType<IUseRouteParams>;
};

export const useConfigComponents = () => {
  const config = useConfig();
  return config?.components;
};

export const useConfigScope = () => {
  const config = useConfig();
  return config?.scope;
};

export const useGetIcon = () => {
  const config = useConfig();
  return config?.getIcon;
};

// 通过 Context 传递配置信息
// 为了可用于 web 小程序 native 等多端，不直接依赖 react-router
// 将报告、通知、API 执行器和路由等配置信息通过 Context 传递
export interface IConfig {
  report?: IReport;
  notifier: INotifier;
  apiExecutor: IAPIExecutor;
  useLocation: IUseLocation;
  useNavigate: IUseNavigate;
  useRouteParams: IUseRouteParams;
  components?: IConfigComponents;
  /** 全局 Schema 表达式 scope，嵌套 ConfigProvider 时浅合并，Entity 局部 scope 优先级更高 */
  scope?: IAnyObject;
  getIcon?: IGetIcon;
}

type Icon = ReactNode | ComponentType

export type IGetIcon = (icon: string) => Icon | Promise<Icon>

export type IUseRouteParams<T extends string | Record<string, string | undefined> = string> = () => Readonly<[T] extends [string] ? Params<T> : Partial<T>>;

export type Params<Key extends string = string> = {
  readonly [key in Key]: string | undefined;
};

export type IUseLocation<T = IAny> = () => {
  pathname: string;
  search: string;
  hash: string;
  state: T;
  key: string;
}

export interface NavigateOptions {
  replace?: boolean;
  state?: IAny;
  preventScrollReset?: boolean;
  relative?: 'route' | 'path';
  unstable_flushSync?: boolean;
  unstable_viewTransition?: boolean;
}

export interface IRoutePath {
  pathname: string;
  search: string;
  hash: string;
}


interface NavigateFunction {
  (to: string | Partial<IRoutePath>, options?: NavigateOptions): Promise<void> | void;
  (delta: number): Promise<void> | void;
}

export type IUseNavigate = () => NavigateFunction;

type ILevel = 'info' | 'warn' | 'error';

export type ISize = 'small' | 'default' | 'large' | string;

type IReport = (info: IAnyObject | Error | unknown, level: ILevel) => void;

export type LoadingComponent = ComponentType<{ size?: ISize, loading?: boolean, children?: ReactNode } & IAnyObject>;

export type ErrorComponent = ComponentType<ErrorProps>;

export type SkeletonComponent = ComponentType<{ size?: ISize, loading?: boolean, children?: ReactNode } & IAnyObject>;

export type IConfigComponents = {
  Loading: LoadingComponent,
  ErrorContent: ErrorComponent,
  Skeleton: SkeletonComponent,
  [key: string]: ComponentType<IAny>
};

export interface ErrorProps {
  returnIndex?: boolean
  loading?: boolean;
  response: IStoreResponse;
  onAgain?: () => IAny | Promise<IAny>;
  icon?: string
  children?: ReactNode
  size?: ISize,
  trace?: string | false
  [key: string]: IAny
}

/**
 * 验证必需字段（仅在无父配置时）
 */
const validateRequiredFields = (config: Partial<IConfig>): void => {
  const requiredFields: Array<keyof IConfig> = ['notifier', 'apiExecutor', 'useLocation', 'useNavigate', 'useRouteParams'];
  const missingFields = requiredFields.filter(field => !config[field]);

  if (missingFields.length > 0) {
    console.error(`ConfigProvider: 当没有上层配置时，value 必须包含所有必需字段。缺少字段: ${missingFields.join(', ')}`);
  }
};

/**
 * 合并 components（浅合并，当前层的组件会覆盖上层的同名组件）
 */
const mergeComponents = (parent: IConfigComponents | undefined, current: IConfigComponents | undefined) => {
  if (parent && current) {
    return { ...parent, ...current };
  }
  return current || parent;
};

/**
 * 合并 scope（浅合并，当前层的 key 会覆盖上层的同名 key）
 */
const mergeScope = (parent: IAnyObject | undefined, current: IAnyObject | undefined) => {
  if (parent && current) {
    return { ...parent, ...current };
  }
  return current || parent;
};

/**
 * 检查配置是否完整（包含所有必需字段）
 */
const isCompleteConfig = (config: Partial<IConfig>): config is IConfig => !!(
  config.notifier
  && config.apiExecutor
  && config.useLocation
  && config.useNavigate
  && config.useRouteParams
);

/**
 * ConfigProvider - 支持自动合并的配置 Provider
 *
 * @summary 配置 Provider，支持多层嵌套时自动合并配置
 * @remarks
 * 当多层挂载时，如果只配置部分字段，会自动读取上一层的配置并合并。
 * 合并规则：
 * - 对于 components / scope 对象，会浅合并（当前层会覆盖上层的同名 key）
 * - 对于其他字段，当前层的优先级更高（如果当前层有值则使用当前层的值）
 * - 如果没有上层配置，当前配置必须包含所有必需字段（notifier, apiExecutor, useLocation, useNavigate, useRouteParams）
 *
 * @remarks
 * **性能优化**：
 * - 如果传入完整配置且没有父配置，会跳过合并逻辑，直接使用原始配置
 * - 只有在需要合并时才会执行合并逻辑
 *
 * @example
 * ```tsx
 * // 完整配置（最常见场景，性能最优）
 * <ConfigProvider value={fullConfig}>
 *   <App />
 * </ConfigProvider>
 *
 * // 部分配置（需要父配置存在）
 * <ConfigProvider value={fullConfig}>
 *   <ConfigProvider value={{ components: customComponents }}>
 *     <App />
 *   </ConfigProvider>
 * </ConfigProvider>
 * ```
 */
/** children 使用 unknown 以兼容 React 18/19 的 ReactNode 类型差异 */
export const ConfigProvider = ({ value, children }: { value: Partial<IConfig> | IConfig; children?: unknown }) => {
  // 读取上层配置
  const parentConfig = useContext(ConfigContext);

  // 合并配置
  const mergedConfig = useMemo<IConfig>(() => {
    // 如果没有父配置
    if (!parentConfig) {
      // 如果配置完整，直接使用（性能优化：跳过验证和合并）
      if (isCompleteConfig(value)) {
        return value;
      }
      // 如果配置不完整，输出错误 在部分场景不需要满足所有字段
      validateRequiredFields(value);
      return value as IConfig;
    }

    // 如果有父配置，执行合并逻辑
    return {
      ...parentConfig,
      ...value,
      components: mergeComponents(parentConfig.components, value.components),
      scope: mergeScope(parentConfig.scope, value.scope),
    };
  }, [parentConfig, value]);

  return <ConfigContextProvider value={mergedConfig}>{children as ReactNode}</ConfigContextProvider>;
};
